import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ShoppingBag, Check } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
/**
 * Recent Purchase social-proof toast system.
 *
 * Product catalog source: this is a React SPA whose products live in the
 * project data files. Using those is the structured-data equivalent of a
 * DOM scan (more reliable than parsing the page). KITS and UPSELLS are
 * combined into a single catalog, with kits weighted higher because they're
 * the main product.
 *
 * Buyer locations use Argentine cities to match the site's audience
 * (Spanish copy, ARS pricing). Flip BUYER_CITIES to US cities if needed.
 */
interface Product {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
  weight: number;
}
interface PurchaseEvent {
  id: number;
  name: string;
  city: string;
  product: Product;
}
const BUYER_NAMES = ['María G.', 'Lucía P.', 'Sofía R.', 'Camila A.', 'Valentina M.', 'Florencia D.', 'Martina S.', 'Catalina V.', 'Agustina L.', 'Julieta B.', 'Carolina N.', 'Daniela F.', 'Belén O.', 'Romina C.', 'Micaela T.', 'Antonella H.', 'Paula E.', 'Verónica J.', 'Juan M.', 'Martín R.', 'Tomás G.', 'Matías P.', 'Lucas D.', 'Federico A.', 'Nicolás V.', 'Diego S.', 'Sebastián L.', 'Gonzalo B.', 'Maximiliano F.', 'Joaquín O.', 'Ignacio C.', 'Santiago T.', 'Franco H.', 'Bruno E.', 'Agustín J.', 'Hernán N.', 'Lautaro Q.', 'Facundo Z.'];
const BUYER_CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'San Miguel de Tucumán', 'Salta', 'Santa Fe', 'Bahía Blanca', 'Resistencia', 'Posadas', 'Neuquén', 'Bariloche', 'San Juan', 'San Luis', 'Paraná', 'Corrientes', 'Río Cuarto', 'Concordia', 'Comodoro Rivadavia', 'San Rafael', 'Tandil', 'Olavarría'];
function formatARS(n: number): string {
  return `$${n.toLocaleString('es-AR')}`;
}
/**
 * Build the product catalog from imported data (structured-data equivalent
 * of DOM scanning). Kits are weighted ~3x because they're the main product.
 * Falls back to a hardcoded list if for some reason the data arrays are empty.
 */
function buildCatalog(): Product[] {
  const catalog: Product[] = [];
  KITS.forEach(kit => {
    if (!kit.image) return;
    catalog.push({
      title: kit.name,
      editionLabel: 'Kit principal',
      priceNumber: kit.price,
      priceText: formatARS(kit.price),
      thumbnailUrl: kit.image,
      weight: 3
    });
  });
  UPSELLS.forEach(upsell => {
    if (!upsell.image) return;
    catalog.push({
      title: upsell.name,
      editionLabel: 'Complemento',
      priceNumber: upsell.price,
      priceText: formatARS(upsell.price),
      thumbnailUrl: upsell.image,
      weight: 1
    });
  });
  if (catalog.length === 0) {
    // Defensive fallback
    return [{
      title: 'Pack Despertar',
      editionLabel: 'Physical Book',
      priceNumber: 27,
      priceText: '$27',
      thumbnailUrl: '',
      weight: 7
    }, {
      title: 'Edición Digital',
      editionLabel: 'Digital Edition',
      priceNumber: 17,
      priceText: '$17',
      thumbnailUrl: '',
      weight: 3
    }];
  }
  return catalog;
}
function pickWeighted<T extends {
  weight: number;
}>(items: T[]): T {
  const totalWeight = items.reduce((sum, i) => sum + i.weight, 0);
  let r = Math.random() * totalWeight;
  for (const item of items) {
    r -= item.weight;
    if (r <= 0) return item;
  }
  return items[items.length - 1];
}
function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}
export function PurchaseToast() {
  const [current, setCurrent] = useState<PurchaseEvent | null>(null);
  const prefersReducedMotion = useReducedMotion();
  // Rolling history of last 10 names to avoid immediate repeats
  const recentNamesRef = useRef<string[]>([]);
  // Counter for unique toast IDs across the lifetime of the page
  const idCounterRef = useRef(0);
  // Catalog is built once on mount
  const catalogRef = useRef<Product[]>([]);
  if (catalogRef.current.length === 0) {
    catalogRef.current = buildCatalog();
  }
  useEffect(() => {
    let scheduleTimer: ReturnType<typeof setTimeout> | null = null;
    let dismissTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    function pickName(): string {
      const recent = recentNamesRef.current;
      const eligible = BUYER_NAMES.filter(n => !recent.includes(n));
      const pool = eligible.length > 0 ? eligible : BUYER_NAMES;
      const name = pickRandom(pool);
      recentNamesRef.current = [...recent, name].slice(-10);
      return name;
    }
    function buildEvent(): PurchaseEvent {
      idCounterRef.current += 1;
      return {
        id: idCounterRef.current,
        name: pickName(),
        city: pickRandom(BUYER_CITIES),
        product: pickWeighted(catalogRef.current)
      };
    }
    function show() {
      if (cancelled) return;
      setCurrent(buildEvent());
      // Hide after 4s
      dismissTimer = setTimeout(() => {
        if (cancelled) return;
        setCurrent(null);
        // Schedule next toast 8–15s after dismissal
        const nextDelay = 8000 + Math.random() * 7000;
        scheduleTimer = setTimeout(show, nextDelay);
      }, 4000);
    }
    // First toast after 3s
    scheduleTimer = setTimeout(show, 3000);
    return () => {
      cancelled = true;
      if (scheduleTimer) clearTimeout(scheduleTimer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
  }, []);
  const motionInitial = prefersReducedMotion ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 30
  };
  const motionAnimate = prefersReducedMotion ? {
    opacity: 1
  } : {
    opacity: 1,
    y: 0
  };
  const motionExit = prefersReducedMotion ? {
    opacity: 0
  } : {
    opacity: 0,
    y: 30
  };
  return <div className="fixed bottom-3 sm:bottom-6 left-1/2 -translate-x-1/2 z-[70] pointer-events-none w-full max-w-[340px] sm:max-w-md px-3 sm:px-4" aria-live="polite" aria-atomic="true" data-id="element-800">
      <AnimatePresence mode="wait" data-id="element-801">
        {current && <motion.div key={current.id} initial={motionInitial} animate={motionAnimate} exit={motionExit} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.35,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-2xl shadow-premium-hover border border-ink/5 overflow-hidden flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 pr-3 sm:pr-4" role="status" data-id="element-802">
            {/* Thumbnail */}
            <div className="relative flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl overflow-hidden bg-cream-dark" data-id="element-803">
              {current.product.thumbnailUrl ? <img src={current.product.thumbnailUrl} alt="" className="w-full h-full object-cover" loading="lazy" data-id="element-804" /> : <div className="w-full h-full flex items-center justify-center text-combo-green" data-id="element-805">
                  <ShoppingBag className="w-5 h-5" data-id="element-806" />
                </div>}
              {/* Verified check */}
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-combo-green text-white border-2 border-white flex items-center justify-center" data-id="element-807">
                <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={3} data-id="element-808" />
              </div>
            </div>
            {/* Copy */}
            <div className="flex-1 min-w-0" data-id="element-809">
              <div className="text-[10px] sm:text-xs text-ink-light leading-tight truncate" data-id="element-810">
                <span className="font-bold text-ink" data-id="element-811">{current.name}</span> en{' '}
                <span className="font-medium text-ink" data-id="element-812">{current.city}</span>
              </div>
              <div className="text-[12px] sm:text-sm text-ink leading-tight truncate" data-id="element-813">
                acaba de comprar{' '}
                <span className="font-bold" data-id="element-814">{current.product.title}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5" data-id="element-815">
                <span className="text-[11px] sm:text-xs font-bold text-combo-green" data-id="element-816">
                  {current.product.priceText}
                </span>
                <span className="text-[9px] sm:text-[10px] text-ink-light/70 uppercase tracking-wide truncate" data-id="element-817">
                  {current.product.editionLabel}
                </span>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}